<template>
    <div>
        <highcharts
            v-if="loaded"
            :options="getOptions(chartOptions)"
            ref="chart"
        />

        <div v-else class="d-flex justify-content-center">
            <b-spinner :style="{'color':constants.spinnerColor}"></b-spinner>
        </div>
    </div>
</template>

<script>
    import {Chart} from 'highcharts-vue'
    import exportingInit from "highcharts/modules/exporting"
    import Highcharts from "highcharts"
    import dashboardChartMixin from '@/mixins/dashboardChartMixin'

    typeof exportingInit === 'function' && exportingInit(Highcharts);

    export default {
        name: 'StartupsCategoryChart',
        mixins: [dashboardChartMixin],
        components: {
            highcharts: Chart
        },
        data() {
            return {
                loaded: false,
                chartName: "StartupsBySectors",
                chartOptions: {
                    chart: {
                        type: "bar",
                        style: {
                            fontFamily: '"Overpass", sans-serif;',
                        },
                    },
                    title: {
                        text: "Sectors Distribution"
                    },
                    xAxis: {
                        categories: [],
                        title: {
                            text: null
                        }
                    },
                    yAxis: {
                        min: 0,
                        title: {
                            text: "Number of startups",
                            align: "high"
                        },
                        labels: {
                            overflow: "justify"
                        }
                    },
                    tooltip: {
                        valueSuffix: " startup(s)"
                    },
                    plotOptions: {
                        bar: {
                            dataLabels: {
                                enabled: true
                            },
                            events: {
                                click: (e) => {
                                    this.openStartupsModal(e.point.series.name, e.point.category)
                                },
                                legendItemClick: function () {
                                    return false
                                }
                            }
                        }
                    },
                    series: [
                        {
                            name: "Category",
                            data: [],
                            color: '#0083b2'
                        }
                    ]
                }
            }
        },
        beforeDestroy() {
            this._removeLegendHandlers()
        },
        methods: {
            fetchChartData(initialCall = false) {
                this.getChartData(this.chartName).then(response => {
                    const series = response.data.series
                    if (series.length > 1) {
                        series[1].visible = false
                    }
                    this.chartOptions.series = series
                    this.chartOptions.xAxis.categories = response.data.categories

                    if (!initialCall) {
                        this.$refs.chart.chart.hideLoading()
                        this.$nextTick(() => {
                            const chart = this.$refs.chart.chart
                            chart.series.forEach((s, i) => s.setVisible(i === 0, false))
                            chart.redraw()
                            this._setupLegendHandlers()
                        })
                    } else {
                        this.loaded = true
                        this.$nextTick(() => {
                            this._setupLegendHandlers()
                        })
                    }
                })
            },

            _setupLegendHandlers() {
                this._removeLegendHandlers()
                const chart = this.$refs.chart?.chart
                if (!chart) return

                const fixedColors = chart.series.map(s => s.userOptions.color || s.color)

                // Fix legend symbol colors on initial render (HC skips hidden series)
                chart.series.forEach((s, i) => {
                    const sym = s.legendItem?.symbol || s.legendSymbol
                    sym?.attr({ fill: fixedColors[i] })
                })

                this._legendClickHandler = (e) => {
                    const clicked = chart.series.find(s => {
                        const el = s.legendItem?.group?.element || s.legendGroup?.element
                        return el?.contains(e.target)
                    })
                    if (!clicked) return
                    e.stopPropagation()
                    if (clicked.visible) return
                    chart.series.forEach(s => s.setVisible(s === clicked, false))
                    chart.redraw()
                    // Fix colors: HC 12 reassigns colorIndex on visibility change
                    chart.series.forEach((s, i) => {
                        const color = fixedColors[i]
                        if (s.visible) {
                            s.points?.forEach(p => p.graphic?.attr({ fill: color }))
                        }
                        const sym = s.legendItem?.symbol || s.legendSymbol
                        sym?.attr({ fill: color })
                    })
                }

                chart.container.addEventListener('click', this._legendClickHandler, true)
            },

            _removeLegendHandlers() {
                const chart = this.$refs.chart?.chart
                if (chart && this._legendClickHandler) {
                    chart.container.removeEventListener('click', this._legendClickHandler, true)
                }
                this._legendClickHandler = null
            },

            openStartupsModal(type, category) {
                this.$emit('showStartups', [this.chartName, this.formatTitle(`Startups in category "${category}"`), JSON.stringify([type, category])])
            },
        }
    }
</script>
